import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api, { API_URL } from './api';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import dayjs from 'dayjs';

const Container = styled.div`
  max-width: 1000px;
  margin: 10px auto;
  padding: 15px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  background: #ffffff;
`;

const Title = styled.h2`
  text-align: center;
  color: #1a1a1a;
  font-weight: 700;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
`;

const ResponsiveTable = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 650px;
  
  th {
    background: #f8f9fa;
    color: #495057;
    font-weight: 600;
    padding: 16px;
    text-align: left;
    border-bottom: 2px solid #dee2e6;
  }
  td {
    padding: 16px;
    border-bottom: 1px solid #eee;
    color: #2d3436;
  }
  tr:hover {
    background-color: #f1f3f5;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 8px;
  text-decoration: none;
  display: inline-block;
  
  &.report {
    background: #e3f2fd;
    color: #1976d2;
    &:hover { background: #bbdefb; }
  }

  &.view {
    background: #f1f3f5;
    color: #495057;
    &:hover { background: #e9ecef; }
  }
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => props.type === 'F' ? '#ffebee' : props.type === 'A' ? '#fff3e0' : '#e8f5e9'};
  color: ${props => props.type === 'F' ? '#c62828' : props.type === 'A' ? '#ef6c00' : '#2e7d32'};
  margin-right: 5px;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid #ddd;
  font-size: 1rem;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: white;
  padding: 25px;
  border-radius: 16px;
  width: 95%;
  max-width: 1100px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 20px 50px rgba(0,0,0,0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

function Docentes() {
  const [docentes, setDocentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [mesReporte, setMesReporte] = useState(dayjs().format('YYYY-MM'));
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchDocentes();
  }, []);

  const fetchDocentes = async () => {
    try {
      const res = await api.get('/docentes');
      setDocentes(res.data);
      setLoading(false);
    } catch (error) {
      Swal.fire('Error', 'No se pudo cargar la lista de docentes', 'error');
    }
  };

  const handleDocente = async (docente = null) => {
    const isEdit = !!docente;
    const { value: formValues } = await Swal.fire({
      title: isEdit ? 'Editar Docente' : 'Nuevo Docente',
      html:
        `<input id="swal-nombres" class="swal2-input" placeholder="Nombres" value="${docente?.nombres || ''}">` +
        `<input id="swal-paterno" class="swal2-input" placeholder="Apellido Paterno" value="${docente?.apellido_paterno || ''}">` +
        `<input id="swal-materno" class="swal2-input" placeholder="Apellido Materno" value="${docente?.apellido_materno || ''}">` +
        `<input id="swal-ci" type="number" class="swal2-input" placeholder="CI" value="${docente?.ci || ''}">` +
        `<input id="swal-item" class="swal2-input" placeholder="Número de Ítem / Código" value="${docente?.item || ''}">` +
        `<input id="swal-especialidad" class="swal2-input" placeholder="Especialidad / Cargo" value="${docente?.especialidad || ''}">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Actualizar' : 'Guardar',
      preConfirm: () => {
        return {
          nombres: document.getElementById('swal-nombres').value,
          apellido_paterno: document.getElementById('swal-paterno').value,
          apellido_materno: document.getElementById('swal-materno').value,
          ci: document.getElementById('swal-ci').value,
          item: document.getElementById('swal-item').value,
          especialidad: document.getElementById('swal-especialidad').value
        }
      }
    });

    if (formValues) {
      if (!formValues.nombres || !formValues.ci || !formValues.item) return Swal.fire('Error', 'Nombre, CI e Ítem son obligatorios', 'warning');
      try {
        if (isEdit) {
          await api.put(`/docentes/${docente.id}`, formValues);
        } else {
          await api.post('/docentes', formValues);
        }
        Swal.fire('Éxito', 'Docente guardado correctamente', 'success');
        fetchDocentes();
      } catch (err) { Swal.fire('Error', 'No se pudo guardar el docente', 'error'); }
    }
  };

  const deleteDocente = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar docente?',
      text: 'Se eliminarán sus horarios y registros asociados.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar'
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/docentes/${id}`);
        Swal.fire('Eliminado', 'El docente fue eliminado', 'success');
        fetchDocentes();
      } catch (err) { Swal.fire('Error', 'No se pudo eliminar', 'error'); }
    }
  };
  const handleReport = async (docente) => {
    const today = new Date().toISOString().split('T')[0];
    const { value: formValues } = await Swal.fire({
      title: `Reportar incidencia para ${docente.nombres}`,
      html:
        '<div style="text-align: left; margin-bottom: 10px; font-weight: 600;">Tipo de incidencia:</div>' +
        '<select id="swal-input1" class="swal2-input" style="width: 85%; margin: 0 auto 20px; display: block;">' +
        '<option value="A" selected>A - Atraso</option>' +
        '<option value="F">F - Falta</option>' +
        '<option value="P">P - Permiso</option>' +
        '</select>' +
        '<div style="text-align: left; margin-bottom: 10px; font-weight: 600;">Fecha:</div>' +
        `<input id="swal-input2" type="date" class="swal2-input" value="${today}" style="width: 85%; margin: 0 auto 20px; display: block;">` +
        '<div style="text-align: left; margin-bottom: 10px; font-weight: 600;">Observación:</div>' +
        '<textarea id="swal-input3" class="swal2-textarea" placeholder="Opcional..." style="width: 85%; margin: 0 auto; display: block;"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar Reporte',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        return [
          document.getElementById('swal-input1').value,
          document.getElementById('swal-input2').value,
          document.getElementById('swal-input3').value
        ]
      }
    });

    if (formValues) {
      const [tipo, fecha, observacion] = formValues;
      if (!fecha) return Swal.fire('Error', 'La fecha es obligatoria', 'warning');
      try {
        await api.post('/asistencias-manuales', { docenteId: docente.id, tipo, fecha, observacion });
        Swal.fire({ icon: 'success', title: 'Reportado', text: 'La incidencia se registró correctamente', timer: 1500, showConfirmButton: false });
      } catch (error) { Swal.fire('Error', 'No se pudo guardar el reporte', 'error'); }
    }
  };

  const previsualizarReporte = async () => {
    setGenerating(true);
    try {
      const res = await api.get(`/registros/validados?mes=${mesReporte}`);
      
      // FILTRO: Solo los que se reportó (incidencias > 0)
      const datosFiltrados = res.data.filter(d => d.faltas > 0 || d.atrasos > 0 || d.permisos > 0);
      
      if (datosFiltrados.length === 0) {
        setGenerating(false);
        return Swal.fire('Sin datos', 'No hay docentes con incidencias reportadas para este mes', 'info');
      }

      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'landscape' });
      const fechaReporte = dayjs(mesReporte + '-01').format('MMMM YYYY').toUpperCase();
      
      doc.setFontSize(16);
      doc.text(`REPORTE DE INCIDENCIAS - ${fechaReporte}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Docentes con faltas, atrasos o permisos`, 14, 22);

      const diasEnMes = dayjs(mesReporte + '-01').daysInMonth();
      
      // Identificar qué días tienen incidencias para solo mostrar esas columnas
      const diasConIncidencias = [];
      for (let i = 1; i <= diasEnMes; i++) {
        const fechaStr = dayjs(mesReporte + '-01').date(i).format('YYYY-MM-DD');
        const hayIncidencia = datosFiltrados.some(d => d.detalle_manual?.some(m => m.fecha === fechaStr));
        if (hayIncidencia) diasConIncidencias.push(i);
      }

      const headers = [['Docente', ...diasConIncidencias.map(d => d.toString()), 'F', 'A', 'P']];
      
      const body = datosFiltrados.map(d => {
        const fila = [d.nombre_completo];
        diasConIncidencias.forEach(i => {
          const fechaStr = dayjs(mesReporte + '-01').date(i).format('YYYY-MM-DD');
          const manual = d.detalle_manual?.find(m => m.fecha === fechaStr);
          if (manual) fila.push(manual.tipo);
          else if (d.asistencias_bio?.includes(fechaStr)) fila.push('.');
          else fila.push('');
        });
        fila.push(d.faltas, d.atrasos, d.permisos);
        return fila;
      });

      // Calcular TOTALES
      const totalF = datosFiltrados.reduce((acc, d) => acc + d.faltas, 0);
      const totalA = datosFiltrados.reduce((acc, d) => acc + d.atrasos, 0);
      const totalP = datosFiltrados.reduce((acc, d) => acc + d.permisos, 0);

      // Añadir fila de totales
      const filaTotales = ['TOTALES GENERALES', ...Array(diasConIncidencias.length).fill(''), totalF, totalA, totalP];
      body.push(filaTotales);

      autoTable(doc, {
        head: headers,
        body: body,
        startY: 28,
        styles: { fontSize: 6, cellPadding: 1, halign: 'center', valign: 'middle', lineWidth: 0.1, lineColor: [200, 200, 200] },
        headStyles: { fillColor: [33, 150, 243], textColor: [255, 255, 255], fontSize: 6.5, fontStyle: 'bold' },
        columnStyles: { 0: { halign: 'left', cellWidth: 35, fontStyle: 'bold' } },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const text = data.cell.text[0];
            if (text === 'F') { data.cell.styles.textColor = [211, 47, 47]; data.cell.styles.fontStyle = 'bold'; }
            if (text === 'A') { data.cell.styles.textColor = [245, 124, 0]; data.cell.styles.fontStyle = 'bold'; }
            if (text === 'P') { data.cell.styles.textColor = [56, 142, 60]; data.cell.styles.fontStyle = 'bold'; }
            
            // Estilo para la fila de totales
            if (data.row.index === body.length - 1) {
              data.cell.styles.fillColor = [240, 240, 240];
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = [0, 0, 0];
            }
          }
        }
      });

      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'No se pudo generar la previsualización', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const closeModal = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setShowModal(false);
  };

  const docentesFiltrados = docentes.filter(d =>
    `${d.nombres} ${d.apellido_paterno} ${d.apellido_materno}`.toLowerCase().includes(filtro.toLowerCase()) ||
    d.ci.toString().includes(filtro) ||
    d.especialidad.toLowerCase().includes(filtro.toLowerCase())
  );

  if (loading) return <div style={{ textAlign: 'center', marginTop: '50px' }}>Cargando docentes...</div>;

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Title style={{ margin: 0 }}>👨‍🏫 Docentes</Title>
        <button
          onClick={() => handleDocente()}
          style={{ background: '#1976d2', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 20px', fontWeight: 'bold', cursor: 'pointer' }}
        >
          + Nuevo Docente
        </button>
      </div>

      <FilterSection>
        <SearchInput
          type="text"
          placeholder="🔍 Buscar por nombre, CI o especialidad..."
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="month"
            value={mesReporte}
            onChange={(e) => setMesReporte(e.target.value)}
            style={{ padding: '10px', borderRadius: '12px', border: '1px solid #ddd', flex: 1 }}
          />
          <button
            onClick={() => window.open(`${API_URL}/registros/reporte-excel?mes=${mesReporte}`, '_blank')}
            style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 15px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Excel
          </button>
          <button
            onClick={previsualizarReporte}
            disabled={generating}
            style={{ background: '#d32f2f', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 15px', fontWeight: 'bold', cursor: 'pointer', opacity: generating ? 0.7 : 1 }}
          >
            {generating ? 'Generando...' : '📄 Reporte'}
          </button>
        </div>
      </FilterSection>

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3 style={{ margin: 0 }}>Previsualización de Reporte - {dayjs(mesReporte + '-01').format('MMMM YYYY').toUpperCase()}</h3>
              <button onClick={closeModal} style={{ background: '#eee', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }}>✕</button>
            </ModalHeader>
            <div style={{ flex: 1, background: '#525659', borderRadius: '8px', overflow: 'hidden' }}>
              <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none' }} title="Vista Previa PDF"></iframe>
            </div>
            <ModalFooter>
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = `Reporte_Incidencias_${mesReporte}.pdf`;
                  link.click();
                }}
                style={{ background: '#d32f2f', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Descargar PDF
              </button>
              <button
                onClick={() => window.open(`${API_URL}/registros/reporte-excel?mes=${mesReporte}`, '_blank')}
                style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Descargar Excel
              </button>
              <button onClick={closeModal} style={{ background: '#f1f3f5', color: '#495057', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 'bold', cursor: 'pointer' }}>
                Cerrar
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      <ResponsiveTable>
        <Table>
          <thead>
            <tr>
              <th></th>
              <th>Información del Docente</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {docentesFiltrados.length > 0 ? (
              docentesFiltrados.map((d) => (
                <tr key={d.id}>
                  <td style={{ width: '50px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1px' }}>
                      <ActionButton as={Link} to={`/reporte-personal/${d.id}?mes=${mesReporte}`} className="view" title="Ver Asistencia">Reporte</ActionButton>
                      {/* <ActionButton as={Link} to={`/calendario/${d.id}`} className="view" title="Ver Horarios">📅</ActionButton> */}
                      <ActionButton className="report" onClick={() => handleReport(d)} title="Incidencia F/A/P">F/A/P</ActionButton>
                      {/* <ActionButton className="view" onClick={() => handleDocente(d)} title="Editar" style={{ color: '#1976d2' }}>✏️</ActionButton> */}
                      {/* <ActionButton className="view" onClick={() => deleteDocente(d.id)} title="Eliminar" style={{ color: '#d32f2f' }}>🗑️</ActionButton> */}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#1a1a1a' }}>
                      {d.nombres} {d.apellido_paterno} {d.apellido_materno}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                      CI: {d.ci} | Ítem: {d.item} | {d.especialidad}
                    </div>
                  </td>
                  <td style={{ width: '150px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
                      {/* <ActionButton as={Link} to={`/reporte-personal/${d.id}?mes=${mesReporte}`} className="view" title="Ver Asistencia">Reporte</ActionButton> */}
                      <ActionButton as={Link} to={`/calendario/${d.id}`} className="view" title="Ver Horarios">📅</ActionButton>
                      {/* <ActionButton className="report" onClick={() => handleReport(d)} title="Incidencia F/A/P">F/A/P</ActionButton> */}
                      <ActionButton className="view" onClick={() => handleDocente(d)} title="Editar" style={{ color: '#1976d2' }}>✏️</ActionButton>
                      <ActionButton className="view" onClick={() => deleteDocente(d.id)} title="Eliminar" style={{ color: '#d32f2f' }}>🗑️</ActionButton>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2" style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                  No se encontraron docentes que coincidan con la búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ResponsiveTable>
    </Container>
  );
}

export default Docentes;
